<script>
import ENUMS from '@shared/messages/enums.json';
import MembershipShareDropdown from '@app/components/MembershipShareDropdown.vue';

export default {
  components: { MembershipShareDropdown },
  analytics: { skip: true },
  head() {
    if (!this.currentMembership) {
      return null;
    }

    setTimeout(() => this.$gtag.pageView(), 200);

    return {
      titleTemplate: `%s - ${this.currentMembership.name} | Teachizy`,
      afterNavigation: () => this.$gtag.pageView(),
    };
  },
  messages: {
    status: ENUMS.membershipStatus,
    statusClass: ENUMS.membershipStatusClass,
  },
  data: () => ({ isLoading: false }),
  created() {
    this.isLoading = true;
    this.$store.dispatch('memberships/find', this.$route.params.uuid)
      .catch(() => this.$errorHandlers.showPage(404, 'Cette page n\'existe pas'))
      .finally(() => (this.isLoading = false));
  },
  destroyed() {
    this.$store.commit('memberships/setCurrent', null);
  },
  computed: {
    currentMembership() {
      return this.$store.state.memberships.current;
    },
    salesURL() {
      return `${this.$store.getters['auth/url']}/abonnements/${this.currentMembership.slug}`;
    },
    schoolUrl() {
      const q = `?p=${this.$store.state.auth.previewToken}`;
      const url = this.$store.getters['auth/url'];
      return `${url}/mon-espace/abonnements/${this.currentMembership.slug}${q}`;
    },
  },
  methods: {
    onViewDraft() {
      const p = `?p=${this.$store.state.auth.previewToken}`;
      this.$buefy.dialog.confirm({
        title: 'Abonnement en brouillon !',
        message: `
          Votre abonnement est en brouillon. <br>
          Le lien auquel vous allez accéder est un lien de prévisualisation :
          <strong>ne le partagez pas, personne d'autre n'y aura accès</strong>
        `,
        type: 'is-warning',
        focusOn: 'cancel',
        onConfirm: () => window.open(`${this.salesURL}${p}`),
      });
    },
    updateStatus(status) {
      const loader = this.$buefy.loading.open();
      this.$store.dispatch('memberships/update', {
        uuid: this.currentMembership.uuid,
        membershipData: { status },
      })
        .then(() => {
          const statusMessages = {
            PUBLISHED: 'Abonnement publié avec succès',
            DRAFT: 'Abonnement mis en brouillon',
            UNLISTED: 'Abonnement mis non répertorié',
            HIDDEN: 'Abonnement caché',
          };
          this.$buefy.toast.open({
            message: statusMessages[status] || 'Statut mis à jour avec succès',
            type: 'is-success',
            duration: 3000,
          });
        })
        .catch((error) => {
          const message = error.response?.data?.message || 'Une erreur est survenue lors de la mise à jour';
          this.$buefy.toast.open({
            message,
            type: 'is-danger',
            duration: 4000,
          });
        })
        .finally(() => loader.close());
    },
    editTitle() {
      this.$buefy.dialog.prompt({
        title: 'Modifier le titre',
        inputAttrs: { value: this.currentMembership.name, maxlength: 128 },
        confirmText: 'Valider',
        onConfirm: (name) => {
          const loader = this.$buefy.loading.open();
          this.$store
            .dispatch('memberships/update', {
              uuid: this.currentMembership.uuid,
              membershipData: { name },
            })
            .finally(() => loader.close());
        },
      });
    },
  },
};
</script>

<template>
  <section class="page">
    <b-loading v-if="!currentMembership || isLoading" active />
    <template v-else>
      <p>
        <router-link :to="{ name: 'memberships' }">
          <b-icon size="is-small" icon="chevron-left" />
          Retour aux abonnements
        </router-link>
      </p>

      <div class="columns is-vcentered mt-0">
        <div class="column">
          <h1 class="title is-3">
            {{ currentMembership.name }}
            <b-button type="is-text" class="tilcomp_quickedit py-0 h-auto valign-middle" icon-left="pencil-alt"
              size="is-small" title="Modifier le titre" @click.stop.prevent="editTitle" />
            <span class="tag is-light mr-3">
              <b-icon class="mr-1 is-size-9" icon="circle"
                :type="$options.messages.statusClass[currentMembership.status]" />
              {{ $options.messages.status[currentMembership.status] }}
            </span>
          </h1>
        </div>

        <div class="column is-narrow">
          <b-button v-if="currentMembership.status == 'DRAFT'" type="is-text" tag="a" @click.prevent="onViewDraft"
            icon-left="eye" class="px-0">
            Aperçu page visiteur
          </b-button>
          <b-button v-else tag="a" :href="salesURL" target="_blank" type="is-text" icon-left="eye" class="px-0">
            Aperçu page visiteur
          </b-button>
          <div class="is-flex is-justify-content-end">
            <b-dropdown class="is-block" position="is-bottom-left" aria-role="menu">
              <template #trigger>
                <b-button icon-right="chevron-down" expanded>
                  <b-icon class="mr-1 is-size-9" icon="circle"
                    :type="$options.messages.statusClass[currentMembership.status]" />
                  {{ $options.messages.status[currentMembership.status] }}
                </b-button>
              </template>
              <b-dropdown-item aria-role="menuitem" @click="updateStatus('PUBLISHED')">
                <p class="is-size-7 has-text-weight-bold">
                  <b-icon icon="circle" type="is-success" class="is-size-9" />
                  Publié
                </p>
                <p class="is-ize-7">
                  Votre abonnement est visible par tous <br>
                  et apparaît dans les listes publiques
                </p>
              </b-dropdown-item>
              <b-dropdown-item aria-role="menuitem" @click="updateStatus('UNLISTED')">
                <p class="is-size-7 has-text-weight-bold">
                  <b-icon icon="circle" type="is-info" class="is-size-9" />
                  Non répertorié
                </p>
                <p class="is-ize-7">
                  Accessible via lien direct uniquement, <br>
                  n'apparaît pas dans les listes publiques
                </p>
              </b-dropdown-item>
              <b-dropdown-item aria-role="menuitem" @click="updateStatus('HIDDEN')">
                <p class="is-size-7 has-text-weight-bold">
                  <b-icon icon="circle" type="is-dark" class="is-size-9" />
                  Caché
                </p>
                <p class="is-ize-7">
                  Visible uniquement par les abonnés existants, <br>
                  nouveaux abonnements désactivés
                </p>
              </b-dropdown-item>
              <b-dropdown-item aria-role="menuitem" @click="updateStatus('DRAFT')">
                <p class="is-size-7 has-text-weight-bold">
                  <b-icon icon="circle" type="is-warning" class="is-size-9" />
                  Brouillon
                </p>
                <p class="is-ize-7">
                  Abonnement UNIQUEMENT visible <br>
                  par vous en mode "Aperçu"
                </p>
              </b-dropdown-item>
            </b-dropdown>

            <MembershipShareDropdown
              :membership="currentMembership"
              position="is-bottom-left"
              aria-role="menu">
              <b-tooltip label="Partager l'abonnement" position="is-left">
                <b-button icon-left="share-square" />
              </b-tooltip>
            </MembershipShareDropdown>
          </div>
        </div>
      </div>

      <div class="tabs mb-10">
        <ul>
          <router-link tag="li" :to="{ name: 'membership_items', params: $route.params }"
            exact-active-class="is-active">
            <a>Contenu</a>
          </router-link>
          <router-link tag="li" :to="{ name: 'membership_settings', params: $route.params }"
            exact-active-class="is-active">
            <a>Paramètres</a>
          </router-link>
          <router-link tag="li" :to="{ name: 'membership_price', params: $route.params }"
            exact-active-class="is-active">
            <a>Prix</a>
          </router-link>
          <router-link tag="li" :to="{ name: 'membership_description', params: $route.params }"
            exact-active-class="is-active">
            <a>Description</a>
          </router-link>
          <router-link tag="li" :to="{ name: 'membership_subscribers', params: $route.params }"
            exact-active-class="is-active">
            <a>Abonnements</a>
          </router-link>
        </ul>
      </div>
      <div>
        <router-view />
      </div>
    </template>
  </section>
</template>
